import React from 'react';
import { Users, Flag, Star, Sparkles, Phone, Instagram } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import Navigation from './Navigation';
import Footer from './Footer';
import { programs, nflFlagPartnership, cheerProgramInfo } from '../mock';

const ProgramsPage = () => {
  // Separate programs by season
  const fallPrograms = programs.filter(p => 
    ['Tackle Football', 'Flag Football', 'Cheerleading'].includes(p.name) || 
    (p.name === '7v7 Football' && p.ages.toLowerCase().includes('girls'))
  );
  
  const springPrograms = programs.filter(p => 
    p.name === 'NFL FLAG' || 
    (p.name === '7v7 Football' && !p.ages.toLowerCase().includes('girls'))
  );

  const getIcon = (name) => {
    if (name.includes('Flag') || name.includes('NFL')) return <Flag size={32} />;
    if (name.includes('Cheer')) return <Star size={32} />;
    if (name.includes('7v7')) return <Sparkles size={32} />;
    return <Users size={32} />;
  };

  const ProgramCard = ({ program }) => (
    <Card key={program.id} className={`program-card ${program.isNew ? 'new-program' : ''}`}>
      <CardHeader>
        {program.isNew && <div className="new-badge">NEW!</div>}
        <div className="program-icon">
          {getIcon(program.name)}
        </div>
        <CardTitle>{program.name}</CardTitle>
        <CardDescription className="program-ages">Ages: {program.ages}</CardDescription>
      </CardHeader>
      <CardContent>
        <p className="program-description">{program.description}</p>
        {program.features && (
          <div className="program-features">
            <ul>
              {program.features.map((feature) => (
                <li key={feature}>{feature}</li>
              ))}
            </ul>
          </div>
        )}
        {program.registrationPeriod && (
          <p className="program-registration">{program.registrationPeriod}</p>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="page-container">
      <Navigation />
      
      <main className="main-content">
        <section className="page-hero">
          <div className="container">
            <h1 className="page-title">Our Programs</h1>
            <p className="page-subtitle">Football and Cheerleading for ages 5-14</p>
          </div>
        </section>

        {/* NFL FLAG Partnership Banner */}
        <section className="nfl-flag-banner">
          <div className="container">
            <div className="flag-banner-content">
              <div className="flag-badge">NEW FOR 2026</div>
              <h2>NFL FLAG Partnership</h2>
              <p>{nflFlagPartnership.description}</p>
              <div className="flag-details">
                <span><strong>Season:</strong> {nflFlagPartnership.seasonDates}</span>
                <span><strong>Registration:</strong> {nflFlagPartnership.registrationPeriod}</span>
              </div>
            </div>
          </div>
        </section>

        {/* Fall Programs */}
        <section className="programs-section fall-programs">
          <div className="container">
            <div className="season-header">
              <h2 className="section-title">Fall Programs</h2>
              <p className="season-subtitle">August - November</p>
            </div>
            <div className="programs-grid">
              {/* Tackle Football */}
              <Card className="program-card">
                <CardHeader>
                  <div className="program-icon">
                    <Users size={32} />
                  </div>
                  <CardTitle>Tackle Football</CardTitle>
                  <CardDescription className="program-ages">Ages: 7u, 8u, 9u, 10u, 11u, 12u, 13u</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="program-description">Competitive tackle football program teaching fundamentals, teamwork, and sportsmanship.</p>
                  <div className="program-features">
                    <ul>
                      <li>Professional Coaching</li>
                      <li>Age-Appropriate Training</li>
                      <li>League Competition</li>
                      <li>Character Development</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>

              {/* Girls Flag Football — NEWEST PROGRAM (featured right after Tackle) */}
              <Card
                className="program-card"
                data-testid="program-card-girls-flag"
                style={{
                  position: 'relative',
                  border: '2px solid #FFD700',
                  boxShadow: '0 12px 40px rgba(255, 215, 0, 0.25)',
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    background: 'linear-gradient(90deg, #FFD700 0%, #FFC500 100%)',
                    color: '#000',
                    padding: '0.3rem 0.7rem',
                    borderRadius: '999px',
                    fontSize: '0.68rem',
                    fontWeight: 900,
                    letterSpacing: '1.5px',
                    boxShadow: '0 4px 12px rgba(255, 215, 0, 0.5)',
                    zIndex: 2,
                  }}
                >
                  ✨ NEWEST PROGRAM
                </span>
                <CardHeader>
                  <div className="program-icon">
                    <Flag size={32} />
                  </div>
                  <CardTitle>Girls Flag Football</CardTitle>
                  <CardDescription className="program-ages">Ages: 12-14 · Girls Only</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="program-description">
                    Our newest program — fast-paced, non-contact flag football built specifically for young women.
                    Team-first, skill-focused, and empowering the next generation of female athletes.
                  </p>
                  <div className="program-features">
                    <ul>
                      <li>Girls-Only Roster</li>
                      <li>No Contact · Skill Focused</li>
                      <li>Speed & Agility Training</li>
                      <li>College-Track Development</li>
                    </ul>
                  </div>
                  <p style={{ marginTop: '0.75rem', color: '#FFD700', fontWeight: 800, fontSize: '0.95rem' }}>
                    Registration: $140
                  </p>
                </CardContent>
              </Card>

              {/* Flag Football */}
              <Card className="program-card">
                <CardHeader>
                  <div className="program-icon">
                    <Flag size={32} />
                  </div>
                  <CardTitle>Flag Football</CardTitle>
                  <CardDescription className="program-ages">Ages: 5-6 years old</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="program-description">Non-contact introduction to football fundamentals for younger players.</p>
                  <div className="program-features">
                    <ul>
                      <li>No Contact</li>
                      <li>Learn Basics</li>
                      <li>Fun Environment</li>
                      <li>Skill Building</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>

              {/* Cheerleading */}
              <Card className="program-card">
                <CardHeader>
                  <div className="program-icon">
                    <Star size={32} />
                  </div>
                  <CardTitle>Cheerleading</CardTitle>
                  <CardDescription className="program-ages">Ages: 5-14 years old</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="program-description">Dynamic cheer program building confidence, athleticism, and team spirit.</p>
                  <div className="program-features">
                    <ul>
                      <li>Stunts & Tumbling</li>
                      <li>Game Day Performances</li>
                      <li>Competition Teams</li>
                      <li>Dance & Cheer</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Spring Programs */}
        <section className="programs-section spring-programs">
          <div className="container">
            <div className="season-header">
              <h2 className="section-title">Spring Programs</h2>
              <p className="season-subtitle">March - June</p>
            </div>
            <div className="programs-grid">
              {/* NFL FLAG */}
              <Card className="program-card new-program">
                <CardHeader>
                  <div className="new-badge">NEW!</div>
                  <div className="program-icon">
                    <Flag size={32} />
                  </div>
                  <CardTitle>NFL FLAG</CardTitle>
                  <CardDescription className="program-ages">Ages: 5-14 years old</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="program-description">Official NFL FLAG football program bringing professional-level youth flag football to the Panthers.</p>
                  <div className="program-features">
                    <ul>
                      <li>Official NFL FLAG Certified</li>
                      <li>Tournament Access</li>
                      <li>Professional Coaching</li>
                      <li>Official NFL Gear</li>
                    </ul>
                  </div>
                  <p className="program-registration">Season: April 4 - June 13, 2026</p>
                  <p className="program-registration">Registration: February 3 - March 14, 2026</p>
                </CardContent>
              </Card>

              {/* Boys 7v7 */}
              <Card className="program-card">
                <CardHeader>
                  <div className="program-icon">
                    <Sparkles size={32} />
                  </div>
                  <CardTitle>Boys 7v7 Football</CardTitle>
                  <CardDescription className="program-ages">Ages: Spring Tackle Players</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="program-description">Fast-paced 7-on-7 football for boys focusing on passing and skill positions.</p>
                  <div className="program-features">
                    <ul>
                      <li>Skill Development</li>
                      <li>Speed Training</li>
                      <li>Position Specific</li>
                      <li>Tournament Play</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Cheer Program Details */}
        {cheerProgramInfo && (
          <section className="cheer-details-section">
            <div className="container">
              <h2 className="section-title">Cheerleading Program Details</h2>
              <Card className="cheer-card" style={{ maxWidth: '800px', margin: '0 auto' }}>
                <CardContent>
                  {/* Coach Alicia Info */}
                  <div style={{ 
                    background: 'rgba(255, 215, 0, 0.1)', 
                    border: '1px solid rgba(255, 215, 0, 0.3)',
                    borderRadius: '12px', 
                    padding: '20px'
                  }}>
                    <h3 style={{ 
                      color: 'var(--panthers-gold)', 
                      marginBottom: '12px',
                      fontSize: '1.2rem'
                    }}>
                      Meet Your Cheer Director
                    </h3>
                    <p style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '1.1rem' }}>
                      Coach Alicia
                    </p>
                    <p style={{ marginBottom: '12px', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                      Cheer Squad Coach | Focus: Confidence Building, Teamwork, and Community Spirit. Coach Alicia brings a vibrant mix of energy and positivity to the Randallstown Panthers. She is committed to mentoring young athletes, helping them develop essential life skills like collaboration and resilience. Her mission is to instill a deep sense of Panther pride, ensuring every girl has the opportunity to shine in athletics and beyond.
                    </p>
                    <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
                      <a href="tel:443-695-8986" style={{ color: 'var(--panthers-gold)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Phone size={16} /> (443) 695-8986
                      </a>
                      <a href="https://www.instagram.com/randallstownpantherscheer" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--panthers-gold)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Instagram size={16} /> @randallstownpantherscheer
                      </a>
                    </div>
                  </div>
                  
                  {/* Cheer Season Flyer */}
                  <div style={{ marginTop: '24px', textAlign: 'center' }}>
                    <img 
                      src="https://customer-assets.emergentagent.com/job_c1db756d-3e00-42a6-869e-ac51c49851dc/artifacts/2xf4w3mw_cheer%202026.jpeg"
                      alt="2026 Cheer Season & Practice Schedule"
                      style={{ 
                        maxWidth: '100%', 
                        borderRadius: '12px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        border: '2px solid rgba(255,215,0,0.3)'
                      }}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default ProgramsPage;
